import { INCOME_STATEMENT_LAYOUT } from './ledger.js';
import { formatMonthYear, formatPeriodRange } from '@packages/format';
import { AS_OF_SERIES_PDF_FIT, asOfSeriesPdfFits, maxAsOfSeriesPdfColumns, mergeSeriesAccounts, mergeSeriesGroupNodes, } from './series-presentation.js';
/** Every group id in the merged tree, depth-first - the same shape
 *  `collectGroupedReportGroupIds` returns for the single-period tree, so both
 *  screens can drive `ReportSettingsMenu`'s expand/collapse-all off the same
 *  `collapsedGroupIds` state shape. */
export function collectSeriesGroupIds(rows) {
    const ids = [];
    const visit = (node) => {
        ids.push(node.id);
        node.groups.forEach(visit);
    };
    for (const row of rows) {
        if (row.kind === 'section')
            row.groups.forEach(visit);
    }
    return ids;
}
/** Column heading for a comparative (Total + Compare) column: the resolved
 *  window it covers, en-ZA dates, matching the vocabulary `ReportRangeControl`
 *  already uses for the committed period - "1 March 2026 – 15 May 2026", not
 *  a generic name like "Year to date". */
function comparativeColumnLabel(statement) {
    return formatPeriodRange(statement.periodStart.slice(0, 10), statement.periodEnd.slice(0, 10));
}
/**
 * Builds the year-on-year comparative columns for Breakdown = Total,
 * Compare = Prior year: the selected financial year (`yearToDate`) beside
 * its prior-year comparatives. Compare is only ever available on an
 * FY-anchored preset (`isCompareAvailable`), so all three fields are
 * populated whenever this is called from the page.
 *
 * **`last-financial-year` collapses to two columns.** When the selected
 * period IS the prior financial year (cutoff = that FY's own last month),
 * `priorYearToDate` (the prior FY-to-the-same-point) and `priorFullYear`
 * (the whole prior FY) resolve to the IDENTICAL window - verified against
 * `resolveReportPeriodSet` (`domains/ledger/ledger-period-set.ts`): both derive
 * from the same `priorFyStartDate`, and `priorYearToDate.to` is a full
 * completed FY's cutoff month-end, which is exactly `priorFullYear.to`
 * (the day before the CURRENT fy's start) too. Rather than trust the preset
 * id, this compares the two windows directly and drops `priorYearToDate`
 * when they agree - so the rule holds for any future period-set input that
 * produces the same coincidence, not only this one named preset.
 */
export function buildComparativeColumns(series) {
    const { yearToDate, priorYearToDate, priorFullYear } = series;
    const columns = [];
    if (yearToDate) {
        columns.push({ label: comparativeColumnLabel(yearToDate), statement: yearToDate });
    }
    const priorYearToDateIsDistinct = !!priorYearToDate &&
        !!priorFullYear &&
        (priorYearToDate.periodStart !== priorFullYear.periodStart || priorYearToDate.periodEnd !== priorFullYear.periodEnd);
    if (priorYearToDate && priorYearToDateIsDistinct) {
        columns.push({ label: comparativeColumnLabel(priorYearToDate), statement: priorYearToDate, separator: true });
    }
    if (priorFullYear) {
        // The separator marks the boundary into the prior-year cluster. When
        // `priorYearToDate` was dropped as a duplicate above, `priorFullYear` is
        // the first (and only) column of that cluster, so it carries the border
        // instead of the (absent) column before it.
        columns.push({
            label: comparativeColumnLabel(priorFullYear),
            statement: priorFullYear,
            separator: !priorYearToDateIsDistinct,
        });
    }
    return columns;
}
/**
 * Builds the ordered column set for a rendered income-statement series -
 * the single place that decides which columns a given `breakdown` shows, so
 * the screen and every export share the same derivation:
 *
 * - `'monthly'`: one column per rolling/FY month (`series.periods`), plus -
 *   only when the series itself carries a prior-year comparative window
 *   (`priorFullYear !== null`, which the caller controls by whether it asked
 *   for `comparatives`) - the YTD/prior-year cluster from
 *   {@link buildComparativeColumns}, matching the "management accounts"
 *   layout. Only the leading separator is Monthly-specific:
 *   `buildComparativeColumns` never puts one on its first column (there are
 *   no month columns ahead of it there), but here that first column is the
 *   boundary between the trailing months and the cluster, so it is added
 *   back presentationally.
 * - `'total'`: {@link buildComparativeColumns} alone - the selected
 *   financial year beside its prior-year comparatives, no month columns.
 */
export function incomeStatementSeriesColumns(series, breakdown) {
    if (breakdown === 'total')
        return buildComparativeColumns(series);
    const monthColumns = series.periods.map((period) => ({
        label: formatMonthYear(period.periodStart.slice(0, 7)),
        statement: period,
    }));
    if (series.priorFullYear === null)
        return monthColumns;
    const [firstComparativeColumn, ...restComparativeColumns] = buildComparativeColumns(series);
    if (!firstComparativeColumn)
        return monthColumns;
    return [...monthColumns, { ...firstComparativeColumn, separator: true }, ...restComparativeColumns];
}
/**
 * Builds the ordered sequence of table rows for the monthly view: the merged
 * account-group tree per section, and the four IFRS subtotal rows - all
 * placed by {@link INCOME_STATEMENT_LAYOUT}, mirroring the single-period
 * view's own placement.
 */
export function buildSeriesTableRows(columns) {
    const rows = [];
    for (const slot of INCOME_STATEMENT_LAYOUT) {
        if (slot.kind === 'subtotal') {
            rows.push({
                kind: 'ifrs-subtotal',
                label: slot.label,
                amountsMinorUnits: columns.map((column) => column.statement[slot.total]),
                strong: slot.strong,
            });
            continue;
        }
        const sectionsByColumn = columns.map((column) => column.statement.sections.find((s) => s.section === slot.key));
        if (sectionsByColumn.every((section) => section === undefined)) {
            continue; // No column has activity in this section - omit it entirely, as the single-period view does.
        }
        const label = sectionsByColumn.find((section) => section !== undefined).label;
        rows.push({
            kind: 'section',
            label,
            groups: mergeSeriesGroupNodes(sectionsByColumn.map((section) => section?.groups ?? [])),
            accounts: mergeSeriesAccounts(sectionsByColumn.map((section) => section?.accounts ?? [])),
            subtotalMinorUnits: sectionsByColumn.map((section) => section?.subtotalMinorUnits ?? 0),
        });
    }
    return rows;
}
// ---------------------------------------------------------------------------
// PDF fit rule
//
// The income statement's names for the shared series page geometry, kept
// because they are what its renderers, its export route and its frontend were
// written against. Aliases, not a second definition: the income-statement
// series and the three as-at series print the same table shape on the same
// page, so a second copy of the arithmetic could only ever drift.
// `apps/backend/platform/src/domains/document-rendering/pdf/styles.ts`'s
// `reportPdfStyles` page padding reads `REPORT_PDF_HORIZONTAL_MARGIN_PT`, the
// same constant the fit subtracts, so a margin change moves the verdict with
// it.
// ---------------------------------------------------------------------------
export const INCOME_STATEMENT_SERIES_PDF_FIT = AS_OF_SERIES_PDF_FIT;
export const maxIncomeStatementSeriesPdfColumns = maxAsOfSeriesPdfColumns;
export const incomeStatementSeriesPdfFits = asOfSeriesPdfFits;
